import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useEffect, useRef } from 'react';
import { createFeatureFlagStore } from "../../store";
import FeatureFlagContext from "../context/FeatureFlagContext";
import transformBasicFlagToFlagObject from "../utils/transformBasicFlagToFlagObject";
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Maximum time (ms) to wait for a valid organizationId before
 * assuming orgId=0 was intentional (for global feature flags).
 */
var ORGANIZATION_ID_TIMEOUT_MS = 15000;
export var FeatureFlagProvider = function FeatureFlagProvider(_ref) {
  var _ref$flags = _ref.flags,
    flags = _ref$flags === void 0 ? [] : _ref$flags,
    organizationId = _ref.organizationId,
    children = _ref.children;
  var storeRef = useRef();
  var timeoutRef = useRef();
  var prevOrgIdRef = useRef(organizationId);
  var fetchedFlagKeysRef = useRef(new Set());
  if (!storeRef.current) {
    storeRef.current = createFeatureFlagStore({
      organizationId: organizationId
    });

    // orgId > 0: fetch immediately, store is already marked as resolved
    if (flags.length > 0 && organizationId > 0) {
      var transformedFlags = transformBasicFlagToFlagObject(flags);
      storeRef.current.getInitialState().fetchAllFlags(transformedFlags);
      fetchedFlagKeysRef.current = new Set(flags.map(function (_ref2) {
        var _ref3 = _slicedToArray(_ref2, 1),
          key = _ref3[0];
        return key;
      }));
    }
  }
  useEffect(function () {
    if (!storeRef.current) {
      return;
    }
    var store = storeRef.current;
    var _store$getState = store.getState(),
      metadata = _store$getState.metadata;
    var orgIdChanged = organizationId !== prevOrgIdRef.current;
    prevOrgIdRef.current = organizationId;

    // A valid organizationId arrived — resolve immediately
    if (organizationId > 0) {
      clearTimeout(timeoutRef.current);

      // Update metadata if orgId changed
      if (metadata.organizationId !== organizationId || !metadata.isOrganizationIdResolved) {
        store.getState().setMetadata({
          organizationId: organizationId,
          isOrganizationIdResolved: true
        });
      }
      if (flags.length > 0) {
        // If orgId changed, re-fetch ALL flags (results differ per org).
        // Otherwise, fetch only flags not yet fetched (new additions).
        var flagsToFetch = orgIdChanged ? flags : flags.filter(function (_ref4) {
          var _ref5 = _slicedToArray(_ref4, 1),
            key = _ref5[0];
          return !fetchedFlagKeysRef.current.has(key);
        });
        if (flagsToFetch.length > 0) {
          var _transformedFlags = transformBasicFlagToFlagObject(flagsToFetch);
          store.getState().fetchAllFlags(_transformedFlags);
        }
        fetchedFlagKeysRef.current = new Set(flags.map(function (_ref6) {
          var _ref7 = _slicedToArray(_ref6, 1),
            key = _ref7[0];
          return key;
        }));
      }
      return;
    }

    // orgId is 0 and already resolved (timeout already fired) — nothing to do
    if (metadata.isOrganizationIdResolved) {
      return;
    }

    // orgId is 0 and not yet resolved — start timeout.
    // When it fires, assume 0 is intentional and proceed.
    if (!timeoutRef.current) {
      timeoutRef.current = setTimeout(function () {
        if (!storeRef.current) {
          return;
        }
        storeRef.current.getState().setMetadata({
          organizationId: 0,
          isOrganizationIdResolved: true
        });
        if (flags.length > 0) {
          var _transformedFlags2 = transformBasicFlagToFlagObject(flags);
          storeRef.current.getState().fetchAllFlags(_transformedFlags2);
          fetchedFlagKeysRef.current = new Set(flags.map(function (_ref8) {
            var _ref9 = _slicedToArray(_ref8, 1),
              key = _ref9[0];
            return key;
          }));
        }
      }, ORGANIZATION_ID_TIMEOUT_MS);
    }
    return function () {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    };
  }, [organizationId, flags]);
  return /*#__PURE__*/_jsx(FeatureFlagContext.Provider, {
    value: storeRef.current,
    children: children
  });
};